import { ActionTree, Commit } from 'vuex';
import { $enum } from 'ts-enum-util';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import api from '@modules/store/upsell/api/index.api';
import {
  SecurityDepositGetDetails,
  SecurityDepositUpdateKYC,
  SecurityDepositGetSigningURL,
  SecurityDepositGet,
} from '@modules/store/upsell/types/UpsellAPI.interface';
import UpsellSecurityDepositState from '@modules/store/upsell/types/UpsellSecurityDepositState.entity';
import { AppRole, AppRoleEnum } from '@modules/husleie-framework/src/types/App.entity';
import { UpsellInstabankKYCError, UpsellStatusEnum } from '@modules/husleie-framework/src/types/Upsell.entity';
import { UpsellSecurityDeposit } from '@modules/husleie-framework/src/types/UpsellSecurityDeposit.entity';

let loadingCount = 0;

function PreAPICall(commit: Commit, role?: AppRole): void {
  commit('common/staticData/setLoading', true, { root: true });
  commit('setUpsellSecurityDepositLoading', true);
  commit('common/staticData/setApiError', { error: false, role }, { root: true });
  commit('setUpsellSecurityDepositApiError', false);
  loadingCount++;
}

function postAPICall(commit: Commit): void {
  loadingCount--;
  if (loadingCount === 0) {
    commit('common/staticData/setLoading', false, { root: true });
    commit('setUpsellSecurityDepositLoading', false);
  }
}

function errorAPICall(commit: Commit, method: string, message: string, role?: AppRole): void {
  commit('common/staticData/setApiError', { error: true, role }, { root: true });
  commit('setUpsellSecurityDepositApiError', true);
  LogOperations.error(`Upsell|SecurityDeposit|Actions|${method}`, message);
}

function generateKYCError(error: string): UpsellInstabankKYCError {
  if (error.includes(UpsellInstabankKYCError.FailedDueTo)) {
    return UpsellInstabankKYCError.FailedDueTo;
  }
  if (error.includes(UpsellInstabankKYCError.UnexpectedResponse)) {
    return UpsellInstabankKYCError.UnexpectedResponse;
  }
  return $enum(UpsellInstabankKYCError).asValueOrDefault(
    error,
    UpsellInstabankKYCError.UnknownError,
  );
}

export const actions: ActionTree<UpsellSecurityDepositState, CommonState> = {
  fetchDepositAccountLegacy: ({ commit }, get: SecurityDepositGet) => {
    PreAPICall(commit, get.role);
    return new api()
      .getDepositAccountLegacy(get)
      .then((data: any) => {
        commit('setUpsellDepositAccount', data.upsellDepositAccount);
      })
      .catch((error: any) => {
//      errorAPICall(commit, 'fetchDepositAccountLegacy', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
    },

  fetchSecurityDeposit: ({ commit }, get: SecurityDepositGet) => {
    PreAPICall(commit, get.role);
    return new api()
      .getSecurityDeposit(get)
      .then((data: any) => {
        commit('setUpsellSecurityDeposit', data.upsellSecurityDeposit);
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchSecurityDepositLegacy', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  fetchSecurityDepositDetails: ({ commit }, get: SecurityDepositGetDetails) => {
    PreAPICall(commit, get.role);
    return new api()
      .getSecurityDepositDetails(get)
      .then((data: any) => {
        const upsellSecurityDeposit: UpsellSecurityDeposit = {
          uuid: data.uuid,
          kyc: data.kyc,
        };
        commit('setUpsellSecurityDeposit', upsellSecurityDeposit);
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchSecurityDepositDetails', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  updateSecurityDepositKYC: ({ commit }, update: SecurityDepositUpdateKYC) => {
    PreAPICall(commit, update.role);
    commit('setUpsellSecurityDeposit', { kycError: undefined });
    return new api()
      .updateSecurityDepositKYC(update)
      .catch((error: any) => {
        const kycError = generateKYCError(error.response?.data?.errors?.[0]?.message || '');
        commit('setUpsellSecurityDeposit', { kycError });
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  fetchSecurityDepositSigningURL: ({ commit }, get: SecurityDepositGetSigningURL) => {
    PreAPICall(commit, get.role);
    return new api()
      .getSecurityDepositSigningURL(get)
      .then((data: any) => {
        if (data.signingURL) {
          commit('setUpsellSecurityDepositSigningURL', data.signingURL);
        } else if (data.redirectURL) {
          commit('setUpsellSecurityDepositRedirectURL', data.redirectURL);
        }

        if (data.dueDateGuaranteeDocumentUrl) {
          commit('setDueDateGuaranteeDocumentUrl', data.dueDateGuaranteeDocumentUrl);
        }
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchSecurityDepositSigning-RedirectURL', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  assignSecurityDepositOpportunity: async ({ commit, dispatch }, { SecuritydepositOpportunity, signingUuid }) => {
    commit('setUpsellSecurityDepositUuid', SecuritydepositOpportunity.uuid);

    // Initial opportunity status, will be overwritten when the 'open' upsell is fetched
    commit('setUpsellSecurityDepositStatus', SecuritydepositOpportunity.status);
    if (SecuritydepositOpportunity.status === UpsellStatusEnum.OPEN) {
      if (SecuritydepositOpportunity.uuid) {
        await dispatch('fetchSecurityDepositLegacy', { uuid: SecuritydepositOpportunity.uuid, role: AppRoleEnum.TENANT });
      } else {
        await dispatch('fetchSecurityDepositDetails', { uuid: signingUuid, role: AppRoleEnum.TENANT });
      }
    }
    commit('setUpsellSecurityDepositLoaded', true);
  },
};
